import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../context/AuthContext';
import { zPasswordFuerte, zPasswordLogin, PASSWORD_MIN_LENGTH } from '../../utils/validations';
import { cuitToLoginEmail } from '../../utils/format';

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z
  .object({
    currentPassword: zPasswordLogin('Contraseña actual'),
    password: zPasswordFuerte('Nueva contraseña'),
    confirmPassword: z.string().min(1, 'Repetí la nueva contraseña'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  })
  .refine((v) => v.password !== v.currentPassword, {
    message: 'La nueva contraseña debe ser distinta a la actual',
    path: ['password'],
  });

type FormValues = z.infer<typeof schema>;

// ── Componente ────────────────────────────────────────────────────────────────

export default function ChangePassword() {
  const { profile } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', password: '', confirmPassword: '' },
  });

  const onSubmit = async (values: FormValues) => {
    setError(null);
    setSuccess(false);

    // Verificar la contraseña actual reautenticando. La identidad de login es el
    // email sintético derivado del CUIT (no el correo de contacto).
    if (profile?.cuit) {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: cuitToLoginEmail(profile.cuit),
        password: values.currentPassword,
      });
      if (signInError) {
        setError('La contraseña actual es incorrecta.');
        return;
      }
    }

    const { error: updateError } = await supabase.auth.updateUser({
      password: values.password,
    });

    if (updateError) {
      setError('No pudimos actualizar la contraseña. Intentá nuevamente.');
      return;
    }

    setSuccess(true);
    reset();
  };

  return (
    <Box sx={{ maxWidth: 520 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Cambiar contraseña
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Para cambiar tu contraseña ingresá la actual y la nueva.
      </Typography>

      <Card>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
                Contraseña actualizada correctamente.
              </Alert>
            )}

            <TextField
              {...register('currentPassword')}
              fullWidth
              required
              label="Contraseña actual"
              type={showPassword ? 'text' : 'password'}
              disabled={isSubmitting}
              autoComplete="current-password"
              error={!!errors.currentPassword}
              helperText={errors.currentPassword?.message}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              {...register('password')}
              fullWidth
              required
              label="Nueva contraseña"
              type={showPassword ? 'text' : 'password'}
              disabled={isSubmitting}
              autoComplete="new-password"
              error={!!errors.password}
              helperText={
                errors.password?.message ??
                `Mínimo ${PASSWORD_MIN_LENGTH} caracteres con al menos un número`
              }
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((p) => !p)}
                      edge="end"
                      size="small"
                      aria-label={showPassword ? 'Ocultar clave' : 'Mostrar clave'}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              {...register('confirmPassword')}
              fullWidth
              required
              label="Repetir nueva contraseña"
              type={showPassword ? 'text' : 'password'}
              disabled={isSubmitting}
              autoComplete="new-password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSubmitting ? 'Actualizando...' : 'Actualizar contraseña'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
